'use client';

// T12 — useMediaAiEnrichment: consolidates the three near-duplicate AI
// enrichment state machines from the legacy media tab (damage
// detection, registration-plate OCR, VIN OCR) into a single hook backed by
// one `Record<photoId, EnrichmentState>` map.
//
// Behavioral contract preserved from the legacy implementation:
//   1. fetch the photo blob from its (presigned) `download_url`
//   2. POST that blob as multipart/form-data to the AI service
//   3. PATCH the validated result to the insurance-service claim photo record
// Each step's failure mode (`Failed to load image` / `"<Kind> failed"`) and
// the 2026-07-17 retry-clears-the-error behavior are preserved exactly.
//
// Which `EnrichmentKind`s apply to a given photo is now looked up from
// `useClaimMediaSlots()`'s `PhotoTypeConfig.enrichments` array (backed by the
// `lkp_claim_photo_type_enrichments` table) instead of hardcoded
// `photo_type === 'front_view'` checks.

import { useCallback, useRef, useState } from 'react';
import type { DamageResult, EnrichmentKind, MediaRecord } from '../types';
import { useClaimMediaSlots } from './useClaimMediaSlots';
import type { RequestContext } from '../requestContext';
import { fetchWithAuthRetry } from '@/lib/auth/fetch-with-auth-retry';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const AI_SVC_URL = process.env.NEXT_PUBLIC_AI_SERVICE_URL || 'http://localhost:8103';
const claimMediaBase = (jobId: string): string => `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}`;

// ── Types ─────────────────────────────────────────────────────────────────

export interface PlateResult {
  plate_number: string | null;
  confidence?: number;
  provider?: string;
}

export interface VinResult {
  vin_number: string | null;
  confidence?: number;
  provider?: string;
}

export type EnrichmentStatus = 'idle' | 'running' | 'success' | 'error';

export interface EnrichmentResults {
  damage?: DamageResult;
  plate?: PlateResult;
  vin?: VinResult;
}

// Per-kind status/error, keyed by EnrichmentKind — added so consumers (e.g.
// MediaDetailPanel's damage section) can read the status of ONE kind without
// being corrupted by a sibling kind's failure on the same photo (e.g.
// front_view runs damage_detection + plate_ocr together; a plate_ocr failure
// must not hide a successful damage_detection result behind a mislabeled
// error, nor make Retry target the wrong kind).
export interface KindState {
  status: EnrichmentStatus;
  error?: string;
}

export interface EnrichmentState {
  // Aggregate status/error across ALL kinds run together for this photo —
  // 'error' if ANY kind failed. Preserved as-is for existing consumers
  // (e.g. AiSummaryPanel's "was this photo's damage assessment successful"
  // check) — new per-kind-sensitive consumers should read `byKind` instead.
  status: EnrichmentStatus;
  results: EnrichmentResults;
  error?: string;
  byKind?: Partial<Record<EnrichmentKind, KindState>>;
}

// The minimal photo shape needed to run enrichments — callers (e.g. the
// upload-success handler wired in Task 20) pass the freshly-uploaded
// MediaRecord straight through.
export type EnrichablePhoto = Pick<MediaRecord, 'id' | 'photo_type' | 'download_url' | 'file_name'>;

export interface UseMediaAiEnrichmentResult {
  enrichmentByPhotoId: Record<string, EnrichmentState>;
  runEnrichments: (photo: EnrichablePhoto) => Promise<void>;
  retryEnrichment: (photo: EnrichablePhoto, kind: EnrichmentKind) => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  const userType = localStorage.getItem('user_type') || '';
  return {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantId,
    'X-User-Type': userType,
  };
}

// Validation gate — malformed/unexpected-shape AI responses must never be
// written to state or PATCHed to the backend.
function isDamageResultShape(value: unknown): value is DamageResult {
  return !!value && typeof value === 'object' && Array.isArray((value as { damages?: unknown }).damages);
}

function isPlateResultShape(value: unknown): value is PlateResult {
  return !!value && typeof value === 'object' && 'plate_number' in (value as Record<string, unknown>);
}

function isVinResultShape(value: unknown): value is VinResult {
  return !!value && typeof value === 'object' && 'vin_number' in (value as Record<string, unknown>);
}

interface EnrichmentEndpoint {
  aiPath: string;
  patchPath: string;
  failureMessage: string;
  validate: (value: unknown) => boolean;
}

const ENRICHMENT_ENDPOINTS: Record<EnrichmentKind, EnrichmentEndpoint> = {
  damage_detection: {
    aiPath: 'detect-damage',
    patchPath: 'damage',
    failureMessage: 'Damage detection failed',
    validate: isDamageResultShape,
  },
  plate_ocr: {
    aiPath: 'extract-plate',
    patchPath: 'plate',
    failureMessage: 'Plate extraction failed',
    validate: isPlateResultShape,
  },
  vin_ocr: {
    aiPath: 'extract-vin',
    patchPath: 'vin',
    failureMessage: 'VIN extraction failed',
    validate: isVinResultShape,
  },
};

function mergeResult(kind: EnrichmentKind, results: EnrichmentResults, value: unknown): EnrichmentResults {
  if (kind === 'damage_detection') return { ...results, damage: value as DamageResult };
  if (kind === 'plate_ocr') return { ...results, plate: value as PlateResult };
  return { ...results, vin: value as VinResult };
}

function nextByKind(
  prevByKind: Partial<Record<EnrichmentKind, KindState>> | undefined,
  kind: EnrichmentKind,
  kindState: KindState,
): Partial<Record<EnrichmentKind, KindState>> {
  return { ...(prevByKind ?? {}), [kind]: kindState };
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useMediaAiEnrichment(jobId: string, context?: RequestContext): UseMediaAiEnrichmentResult {
  const { data: config } = useClaimMediaSlots();
  const [enrichmentByPhotoId, setEnrichmentByPhotoId] = useState<Record<string, EnrichmentState>>({});

  // Tracks in-flight kind counts per photo across the set of enrichments a
  // single runEnrichments/retryEnrichment call fires in parallel, so the last
  // one to settle can compute the photo's final aggregate status.
  const pendingCounts = useRef<Record<string, number>>({});

  const applicableKinds = useCallback(
    (photoType: string | undefined): EnrichmentKind[] => {
      if (!photoType || !config) return [];
      const match = config.photoTypes.find(photoTypeConfig => photoTypeConfig.code === photoType);
      return match?.enrichments ?? [];
    },
    [config],
  );

  const runOneEnrichment = useCallback(
    async (photo: EnrichablePhoto, kind: EnrichmentKind): Promise<void> => {
      const { id: photoId, download_url: downloadUrl, file_name: fileName } = photo;
      const endpoint = ENRICHMENT_ENDPOINTS[kind];

      try {
        if (!downloadUrl) {
          setEnrichmentByPhotoId(prev => {
            const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
            return {
              ...prev,
              [photoId]: {
                ...base,
                status: 'error',
                error: 'Failed to load image',
                byKind: nextByKind(base.byKind, kind, { status: 'error', error: 'Failed to load image' }),
              },
            };
          });
          return;
        }

        // 1. Fetch the image blob from the (presigned) MinIO download URL.
        const imageResponse = await fetch(downloadUrl);
        if (!imageResponse.ok) {
          setEnrichmentByPhotoId(prev => {
            const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
            return {
              ...prev,
              [photoId]: {
                ...base,
                status: 'error',
                error: 'Failed to load image',
                byKind: nextByKind(base.byKind, kind, { status: 'error', error: 'Failed to load image' }),
              },
            };
          });
          return;
        }
        const blob = await imageResponse.blob();

        // 2. Send the blob to the AI service for this enrichment kind.
        const formData = new FormData();
        formData.append('file', blob, fileName || 'photo.jpg');
        const aiResponse = await fetch(`${AI_SVC_URL}/api/v1/ai/document/${endpoint.aiPath}`, {
          method: 'POST',
          body: formData,
        });
        if (!aiResponse.ok) {
          setEnrichmentByPhotoId(prev => {
            const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
            return {
              ...prev,
              [photoId]: {
                ...base,
                status: 'error',
                error: endpoint.failureMessage,
                byKind: nextByKind(base.byKind, kind, { status: 'error', error: endpoint.failureMessage }),
              },
            };
          });
          return;
        }

        const result: unknown = await aiResponse.json();

        // Validation gate — reject malformed/unexpected-shape responses
        // before writing to state or PATCHing the backend.
        if (!endpoint.validate(result)) {
          setEnrichmentByPhotoId(prev => {
            const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
            return {
              ...prev,
              [photoId]: {
                ...base,
                status: 'error',
                error: endpoint.failureMessage,
                byKind: nextByKind(base.byKind, kind, { status: 'error', error: endpoint.failureMessage }),
              },
            };
          });
          return;
        }

        // 3. Persist the validated result to the insurance service. Uses
        // fetchWithAuthRetry (unlike the two fetches above, which hit the
        // AI service directly and need no auth) -- this PATCH is the one
        // step that can silently lose a freshly-computed AI result to a
        // stale access token: a plain fetch() here would fail with a
        // generic "<Kind> failed" that masks the real (auth) cause and
        // gives no path to recover without the user noticing and manually
        // retrying this one photo.
        const patchBase = context ? context.baseUrl : claimMediaBase(jobId);
        const patchHeaders = context ? context.headers : authHeaders();
        await fetchWithAuthRetry(`${patchBase}/photos/${photoId}/${endpoint.patchPath}`, {
          method: 'PATCH',
          headers: { ...patchHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(result),
        });

        setEnrichmentByPhotoId(prev => {
          const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
          return {
            ...prev,
            [photoId]: {
              ...base,
              results: mergeResult(kind, base.results, result),
              byKind: nextByKind(base.byKind, kind, { status: 'success' }),
            },
          };
        });
      } catch {
        setEnrichmentByPhotoId(prev => {
          const base = prev[photoId] ?? { status: 'running' as EnrichmentStatus, results: {} };
          return {
            ...prev,
            [photoId]: {
              ...base,
              status: 'error',
              error: endpoint.failureMessage,
              byKind: nextByKind(base.byKind, kind, { status: 'error', error: endpoint.failureMessage }),
            },
          };
        });
      } finally {
        const remaining = (pendingCounts.current[photoId] ?? 1) - 1;
        pendingCounts.current[photoId] = remaining;
        if (remaining <= 0) {
          // Recompute the aggregate status by scanning the CURRENT byKind map
          // (all kinds ever tracked for this photo), not just this batch —
          // so retrying only the one kind that actually failed correctly
          // clears the aggregate error, without depending on every retry
          // being perfectly kind-scoped.
          setEnrichmentByPhotoId(prev => {
            const base = prev[photoId] ?? { results: {} };
            const kindStates = Object.values(base.byKind ?? {});
            const finalStatus: EnrichmentStatus = kindStates.some(k => k?.status === 'error') ? 'error' : 'success';
            return { ...prev, [photoId]: { ...base, status: finalStatus } };
          });
        }
      }
    },
    [jobId, context],
  );

  const runKinds = useCallback(
    async (photo: EnrichablePhoto, kinds: EnrichmentKind[]): Promise<void> => {
      if (kinds.length === 0) return;
      const { id: photoId } = photo;
      pendingCounts.current[photoId] = kinds.length;
      setEnrichmentByPhotoId(prev => {
        const base = prev[photoId];
        let byKind = base?.byKind ?? {};
        kinds.forEach(kind => {
          byKind = nextByKind(byKind, kind, { status: 'running' });
        });
        return {
          ...prev,
          [photoId]: { status: 'running', results: base?.results ?? {}, error: undefined, byKind },
        };
      });
      await Promise.all(kinds.map(kind => runOneEnrichment(photo, kind)));
    },
    [runOneEnrichment],
  );

  const runEnrichments = useCallback(
    async (photo: EnrichablePhoto): Promise<void> => {
      const kinds = applicableKinds(photo.photo_type);
      await runKinds(photo, kinds);
    },
    [applicableKinds, runKinds],
  );

  const retryEnrichment = useCallback(
    async (photo: EnrichablePhoto, kind: EnrichmentKind): Promise<void> => {
      await runKinds(photo, [kind]);
    },
    [runKinds],
  );

  return { enrichmentByPhotoId, runEnrichments, retryEnrichment };
}
